import { useLayoutEffect, useRef, memo } from "react"
import Select from "react-select"
import { FaUndo } from "react-icons/fa"
import { IoMdSettings } from "react-icons/io"
import { CheckBox, Input } from "../../components/Form"
import {
  DataSheetGrid as ReactDataSheet,
  checkboxColumn,
  keyColumn,
  textColumn,
} from "react-datasheet-grid"
import { Label, SelectInput } from "../../components/Form"
import { Card } from "../../components/Card"
import { ModalBody, ModalFooter, ModalHeader, ModalPanel } from "../../components/Modal"
import { Button, ButtonLoader } from "../../components/Button"
import { ActionsButtons } from "../../components/ActionsButtons"
import "react-datasheet-grid/dist/style.css"
import "./styles.scss"

const SelectComponent = memo(({ active, rowData, focus, stopEditing, columnData, ...props }) => {
  const ref = useRef(null)
  const isMulti = rowData.questionType !== "radio" && rowData?.isMulti

  useLayoutEffect(() => {
    if (focus) {
      ref.current?.focus()
    } else {
      ref.current?.blur()
    }
  }, [focus])

  const getValue = () => {
    if (isMulti) {
      return rowData?.answer
        ? rowData?.answer.split("|").map((row) => ({ value: row, label: row }))
        : []
    }

    return rowData?.answer ? { value: rowData?.answer, label: rowData?.answer } : null
  }

  return (
    <Select
      ref={ref}
      styles={{
        container: (provided) => ({
          ...provided,
          maxWidth: 220,
          alignSelf: "stretch",
          pointerEvents: focus ? undefined : "none",
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: 9999999 }),
        menu: (provided) => ({ ...provided, zIndex: 999999 }),
        control: (provided) => ({
          ...provided,
          height: "100%",
          border: "none",
          boxShadow: "none",
          background: "none",
        }),
        indicatorSeparator: (provided) => ({
          ...provided,
          opacity: 0,
        }),
        indicatorsContainer: (provided) => ({
          ...provided,
          opacity: active ? 1 : 0,
        }),
        placeholder: (provided) => ({
          ...provided,
          opacity: active ? 1 : 0,
        }),
      }}
      isDisabled={columnData.disabled}
      value={getValue()}
      isMulti={isMulti}
      menuPortalTarget={document.body}
      menuIsOpen={focus}
      onChange={(choice) => {
        let newRowData = { ...rowData }

        if (choice && choice.length) {
          newRowData = { ...rowData, answer: choice.map((row) => row.value).join("|") }
        } else if (choice) {
          newRowData = { ...rowData, answer: choice?.value }
        }

        columnData.handleChange(newRowData, props, "select")
      }}
      onMenuClose={() => stopEditing({ nextRow: true })}
      options={rowData.options ? rowData.options.map((row) => ({ value: row, label: row })) : []}
    />
  )
})

const selectColumn = (options) => ({
  component: SelectComponent,
  columnData: options,
  disableKeys: true,
  keepFocus: true,
  disabled: options.disabled,
})

const commonColumns = [
  { ...keyColumn("id", textColumn), disabled: true, title: "Atendimento", maxWidth: 120 },
  { ...keyColumn("date", textColumn), disabled: true, title: "Data", maxWidth: 140 },
  { ...keyColumn("name", textColumn), disabled: true, title: "Produtor", maxWidth: 160 },
  { ...keyColumn("question", textColumn), disabled: true, title: "Questão" },
  { ...keyColumn("answer", textColumn), title: "Resposta" },
  // { ...keyColumn("answer", textColumn), title: "Resposta", component: AnswerCell }
]

const columns = [
  { ...keyColumn("userSelect", checkboxColumn), title: "", maxWidth: 50 },
  ...commonColumns,
]

const updatedColumns = [
  { ...keyColumn("name", textColumn), disabled: true, title: "Produtor" },
  { ...keyColumn("updatedData", textColumn), disabled: true, title: "Alterado em" },
  { ...keyColumn("question", textColumn), disabled: true, title: "Questão" },
  { ...keyColumn("oldAnswer", textColumn), disabled: true, title: "Resposta Antiga" },
  { ...keyColumn("newAnswer", textColumn), disabled: true, title: "Nova Resposta" },
]

const FilterModal = ({
  modalIsOpen,
  setModalIsOpen,
  onChange,
  control,
  loading,
  values,
  handleSearchData,
  scopeOptions,
  dw,
}) => {
  return (
    <ModalPanel modalIsOpen={modalIsOpen} width={500}>
      <ModalHeader closeModal={() => setModalIsOpen(false)} title="Filtro"></ModalHeader>
      <ModalBody>
        <div className="scroll-select-modal">
          <Label label="Filtrar por: " />
          <SelectInput
            handleChange={onChange("filterType")}
            isSearchable={false}
            name="filterType"
            options={[
              { label: "Organizações", value: "organization" },
              { label: "Empresas", value: "company" },
              { label: "Projetos", value: "project" },
            ]}
            isOverModal={true}
            isClearable
            defaultValue={values?.filterType}
          />
          <Label label={values?.filterType?.label || "Organizações"} />
          <SelectInput
            handleChange={onChange("scope")}
            closeMenuOnSelect={false}
            isSearchable
            isClearable
            isMulti
            isOverModal={true}
            name="scope"
            options={scopeOptions || []}
            control={control}
            isDisabled={loading}
            defaultValue={values?.scope || []}
            value={values?.scope}
            resetFields={!values?.scope?.length}
          />
          <Label label="Usuários" />
          <SelectInput
            handleChange={onChange("users")}
            closeMenuOnSelect={false}
            isSearchable
            isClearable
            isMulti
            isOverModal={true}
            name="users"
            options={
              dw?.farmers?.sort((a, b) => {
                return a.label.localeCompare(b.label)
              }) || []
            }
            control={control}
            isDisabled={loading}
            defaultValue={values?.users || []}
            resetFields={!values?.scope || !values?.scope.length || values?.filterType}
          />
          <Label label="Formulários" />
          <SelectInput
            handleChange={onChange("forms")}
            closeMenuOnSelect={false}
            isSearchable
            isClearable
            isMulti
            isOverModal={true}
            name="forms"
            options={dw?.forms || []}
            control={control}
            isDisabled={loading}
            defaultValue={values?.forms || []}
            resetFields={!values?.scope || !values?.scope.length || values?.filterType}
          />
          <Label label="Etapas" />
          <SelectInput
            handleChange={onChange("steps")}
            closeMenuOnSelect={false}
            isSearchable
            isClearable
            isMulti
            isOverModal={true}
            name="steps"
            options={dw?.steps || []}
            control={control}
            isDisabled={loading}
            defaultValue={values?.steps || []}
            resetFields={!values?.forms || !values?.forms.length}
          />
          <Label label="Questões" />
          <SelectInput
            handleChange={onChange("questions")}
            closeMenuOnSelect={false}
            isSearchable
            isClearable
            isMulti
            isOverModal={true}
            name="questions"
            options={dw?.questions || []}
            control={control}
            isDisabled={loading}
            defaultValue={values?.questions || []}
            resetFields={!values?.steps || !values?.steps.length}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <ButtonLoader
          className="button-engagement"
          messageProcess="Buscando dados..."
          loading={loading}
          handleClick={handleSearchData}
          color="orange"
        />
      </ModalFooter>
    </ModalPanel>
  )
}

const SettingsModal = ({ modalIsOpen, setModalIsOpen, onChange, values }) => {
  return (
    <ModalPanel modalIsOpen={modalIsOpen} width={500}>
      <ModalHeader closeModal={setModalIsOpen} title="Configurações" />
      <ModalBody>
        <div className="scroll-select-modal">
          <Label label="Ordenar por: " />
          <SelectInput
            handleChange={onChange("orderByColumn")}
            isSearchable
            name="orderBy"
            options={[
              { label: "Atendimentos", value: "id" },
              { label: "Data de atualização", value: "date" },
              { label: "Nome do produtor", value: "name" },
              { label: "Título da questão", value: "question" },
              { label: "Resposta", value: "answer" },
            ]}
            isOverModal
            isClearable
            defaultValue={values?.orderByColumn}
          />
          <Label label="Tipo de ordenação" />
          <SelectInput
            handleChange={onChange("orderByType")}
            isSearchable
            name="orderBy"
            options={[
              { label: "Ordem Crescente", value: "asc" },
              { label: "Ordem Decrescente", value: "desc" },
            ]}
            isOverModal
            isClearable
            defaultValue={values?.orderByType}
          />
        </div>
      </ModalBody>
    </ModalPanel>
  )
}

export const DataSheetGridScreen = ({
  data,
  handleChange,
  isFilterOpen,
  handleOpenFilter,
  handleCloseFilter,
  handleChangeFilter,
  scopeOptions,
  loading,
  filterData,
  dw,
  handleSearchData,
  rowsToUpdate,
  handleSimpleFilter,
  handleCancelUpdates,
  selectedUsers,
  handleChangeDeleteRow,
  rowsToDelete,
  handleRecoveryRow,
  handleOpenSettings,
  handleCloseSettings,
  isSettingsOpen,
  handleChangeSettings,
  settings,
  cellOnFocus,
  setCellOnFocus,
  handleSaveChanges,
  handleEmptyAnswers,
  onlyEmptyAnswers,
  handleSelectAll,
  selectAll,
}) => {
  return (
    <div className="data_sheet__container">
      {data ? (
        <>
          <div className="sheet_filter__container">
            <div className="filter__content">
              <Input
                placeholder="Filtrar por produtor, questão ou resposta"
                onChange={(value) => handleSimpleFilter(value)}
                hasIcon={<i class="fi fi-rr-search"></i>}
                hasFocus={false}
                isWhite
              />
            </div>
            <div className="button__container">
              <ActionsButtons
                {...{
                  countUsers: selectedUsers.length,
                  selectedsUsers: selectedUsers,
                }}
              />
              <Button handleClick={handleOpenFilter} color="orange" label="Filtrar" />
              <button onClick={handleOpenSettings} className="sheet_settings">
                <IoMdSettings size={24} />
              </button>
            </div>
          </div>
          <div className="content__options">
            <CheckBox
              label="Trazer apenas respostas vazias"
              checked={onlyEmptyAnswers}
              onChange={handleEmptyAnswers}
            />
            <CheckBox label="Selecionar todos" checked={selectAll} onChange={handleSelectAll} />
          </div>
          <div className="sheet_card__container">
            <div className="sheet_header__container">
              <h5>Editar Respostas dos atendimentos (Texto, URL, Localização)</h5>
              {cellOnFocus?.text &&
              (cellOnFocus?.text?.row || cellOnFocus?.text?.row == 0) &&
              cellOnFocus?.text?.colId ? (
                <h6>
                  Selecionado:{" "}
                  {(data["text"] &&
                    data["text"][cellOnFocus?.text?.row] &&
                    data["text"][cellOnFocus?.text?.row][cellOnFocus?.text?.colId]) ||
                    ""}
                </h6>
              ) : (
                <></>
              )}
            </div>
            <ReactDataSheet
              height={400}
              value={data["text"]}
              onChange={handleChange}
              columns={columns}
              onActiveCellChange={({ cell }) =>
                setCellOnFocus((current) => ({ ...current, text: cell }))
              }
              cellClassName={({ rowData }) =>
                rowsToUpdate.find((row) => row.fsqId === rowData.fsqId) && "bg-updated"
              }
              stickyRightColumn={{
                component: ({ rowData }) => (
                  <button
                    className="sheet_delete_button"
                    onClick={() => handleChangeDeleteRow(rowData)}>
                    ❌
                  </button>
                ),
              }}
              lockRows
            />
          </div>
          <div className="sheet_card__container with_margin">
            <div className="sheet_header__container">
              <h5>Editar Respostas dos atendimentos (Lista Suspensa e Escolha Única)</h5>
              {cellOnFocus.select ? (
                <h6>
                  Selecionado:{" "}
                  {data["select"][cellOnFocus?.select?.row][cellOnFocus?.select?.colId || "answer"]}
                </h6>
              ) : (
                <></>
              )}
            </div>
            <ReactDataSheet
              height={400}
              value={data["select"]}
              onChange={(rows, updated) => handleChange(rows, updated, "text-select")}
              columns={[
                ...columns.filter((row) => row.title !== "Resposta"),
                { ...selectColumn({ handleChange }), title: "Resposta", maxWidth: 200 },
              ]}
              cellClassName={({ rowData }) =>
                rowsToUpdate.find((row) => row.fsqId === rowData.fsqId) && "bg-updated"
              }
              onActiveCellChange={({ cell }) =>
                setCellOnFocus((current) => ({ ...current, select: cell }))
              }
              stickyRightColumn={{
                component: ({ rowData }) => (
                  <button
                    className="sheet_delete_button"
                    onClick={() => handleChangeDeleteRow(rowData, "select")}>
                    ❌
                  </button>
                ),
              }}
              lockRows
            />
          </div>
          {rowsToUpdate && rowsToUpdate.length ? (
            <div className="sheet_card__container with_margin">
              <div className="sheet_header__container">
                <h5>Respostas Alteradas</h5>
              </div>
              <ReactDataSheet value={rowsToUpdate} columns={updatedColumns} lockRows />
            </div>
          ) : (
            <></>
          )}
          {rowsToDelete && rowsToDelete.length ? (
            <div className="sheet_card__container with_margin">
              <div className="sheet_header__container">
                <h5>Respostas removidas</h5>
              </div>
              <ReactDataSheet
                value={rowsToDelete}
                columns={commonColumns.map((row) => ({ ...row, disabled: true }))}
                lockRows
                stickyRightColumn={{
                  component: (
                    { rowData }, // onClick={deleteRow}
                  ) => (
                    <button
                      className="sheet_delete_button"
                      onClick={() => handleRecoveryRow(rowData)}>
                      <FaUndo />
                    </button>
                  ),
                }}
              />
            </div>
          ) : (
            <></>
          )}
          <div className="sheet_button__container">
            <ButtonLoader
              className="button-engagement"
              loading={loading}
              label="Cancelar"
              isDisabled={
                !((rowsToUpdate && rowsToUpdate.length) || (rowsToDelete && rowsToDelete.length))
              }
              handleClick={handleCancelUpdates}
              color="orange"
            />
            <ButtonLoader
              className="button-engagement"
              label="Salvar Alterações"
              loading={loading}
              isDisabled={
                !((rowsToUpdate && rowsToUpdate.length) || (rowsToDelete && rowsToDelete.length))
              }
              handleClick={handleSaveChanges}
            />
          </div>
        </>
      ) : (
        <></>
      )}
      {!data ? (
        <Card className="no-border">
          <div className="analystic-table__button">
            <Button handleClick={handleOpenFilter} color={"orange"} label="Iniciar" />
          </div>
        </Card>
      ) : (
        <></>
      )}
      <FilterModal
        modalIsOpen={isFilterOpen}
        setModalIsOpen={handleCloseFilter}
        onChange={handleChangeFilter}
        scopeOptions={scopeOptions}
        loading={loading}
        dw={dw}
        values={filterData}
        handleSearchData={handleSearchData}
      />
      <SettingsModal
        modalIsOpen={isSettingsOpen}
        setModalIsOpen={handleCloseSettings}
        onChange={handleChangeSettings}
        values={settings}
      />
    </div>
  )
}
